// Interface normalizada para uso na aplicação
export interface IBGECity {
  id: number;
  nome: string;
  uf: string;
  displayName: string; // "Cidade - UF"
}

// Interface da resposta bruta da API (modo nivelado)
interface IBGECityRaw {
  "municipio-id": number;
  "municipio-nome": string;
  "UF-sigla": string;
}

// Cache global
let citiesCache: IBGECity[] | null = null;
let fetchPromise: Promise<IBGECity[]> | null = null;

// Função para converter texto para Title Case
export const toTitleCase = (text: string): string => {
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Função para remover acentos para busca
export const removeAccents = (text: string): string => {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// Função para buscar e normalizar todas as cidades do Brasil
export async function fetchAllCities(): Promise<IBGECity[]> {
  if (citiesCache) return citiesCache;
  if (fetchPromise) return fetchPromise;

  fetchPromise = fetch(
    "https://servicodados.ibge.gov.br/api/v1/localidades/municipios?view=nivelado&orderBy=nome"
  )
    .then(res => {
      if (!res.ok) throw new Error("Erro ao buscar cidades do IBGE");
      return res.json();
    })
    .then((data: IBGECityRaw[]) => {
      const normalized: IBGECity[] = data.map(city => ({
        id: city["municipio-id"],
        nome: toTitleCase(city["municipio-nome"]),
        uf: city["UF-sigla"],
        displayName: `${toTitleCase(city["municipio-nome"])} - ${city["UF-sigla"]}`
      }));
      citiesCache = normalized;
      return normalized;
    })
    .catch(error => {
      console.error("Erro ao buscar cidades:", error);
      fetchPromise = null;
      return [];
    });

  return fetchPromise;
}

// Função para buscar cidades filtradas por UF
export async function fetchCitiesByState(uf: string): Promise<IBGECity[]> {
  const allCities = await fetchAllCities();
  return allCities.filter(city => city.uf === uf);
}

// Função para filtrar cidades por termo de busca
export function filterCities(cities: IBGECity[], search: string, limit = 100): IBGECity[] {
  if (!search) return cities.slice(0, limit);
  
  const searchNormalized = removeAccents(search.toLowerCase());
  return cities
    .filter(city => removeAccents(city.displayName.toLowerCase()).includes(searchNormalized))
    .slice(0, limit);
}
